import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  ICourseDetails,
  ICourseProgress,
  courseApi,
  tPromiseFromFunctionResolved
} from 'common/modules/ReactShared';
import wistiaApi from 'students/api/wistia';

interface ICoursePageState {
  courseDetails: ICourseDetails | null;
  courseProgress: ICourseProgress | null;
}

interface ILearningMaterialsState {
  [key: number]: ICoursePageState;
}

const initialState: ICoursePageState = {
  courseDetails: null,
  courseProgress: null
};

const learningMaterialsInitialState: ILearningMaterialsState = {};

const getCourse = createAsyncThunk<
  tPromiseFromFunctionResolved<typeof courseApi.fetchCourse>['course'],
  ICourseDetails['slug']
>('public/getCourse_request', async (slug) => {
  const { course } = await courseApi.fetchCourse(slug);

  if (!course.videoURL) {
    return course;
  }

  return {
    ...course,
    videoURL: await wistiaApi.resolvePlayableVideoUrl(course.videoURL)
  };
});

const getCourseProgress = createAsyncThunk<
  tPromiseFromFunctionResolved<typeof courseApi.fetchCourseProgress>,
  ICourseDetails['slug']
>('homePage/getCourseProgress_request', async (courseSlug) => {
  const data = await courseApi.fetchCourseProgress(courseSlug);
  return data;
});

const coursePageSlice = createSlice({
  name: 'coursePage',
  initialState: initialState as ICoursePageState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getCourse.pending, () => initialState);

    builder.addCase(getCourse.fulfilled, (state, action) => ({
      ...state,
      courseDetails: action.payload
    }));

    builder.addCase(getCourse.rejected, () => initialState);

    builder.addCase(getCourseProgress.pending, (state) => ({
      ...state,
      courseProgress: null
    }));

    builder.addCase(getCourseProgress.fulfilled, (state, action) => ({
      ...state,
      courseProgress: action.payload.courseProgress
    }));

    builder.addCase(getCourseProgress.rejected, (state) => ({
      ...state,
      courseProgress: null
    }));
  }
});

const learningMaterialsSlice = createSlice({
  name: 'learningMaterials',
  initialState: learningMaterialsInitialState as ILearningMaterialsState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getCourse.fulfilled, (state, action) => ({
      ...state,
      [action.payload.id]: {
        courseDetails: action.payload,
        courseProgress: state[action.payload.id]?.courseProgress || null
      }
    }));

    builder.addCase(getCourseProgress.fulfilled, (state, action) => ({
      ...state,
      [action.payload.courseProgress.courseId]: {
        courseProgress: action.payload.courseProgress,
        courseDetails:
          state[action.payload.courseProgress.courseId]?.courseDetails || null
      }
    }));
  }
});

const { reducer: courseReducer } = coursePageSlice;
const { reducer: learningMaterialsReducer } = learningMaterialsSlice;

const courseActions = { getCourse, getCourseProgress };

export { courseActions, learningMaterialsReducer };
export default courseReducer;
export type { ICoursePageState, ILearningMaterialsState };
